import DownloadFilled from '@icons/DownloadFilled';
import { Box, Fab, Typography, useTheme } from '@mui/material';
import { TackleboxLink, UserNotification } from '@types';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { sec } from '../../security';

const { VITE_SERVICE_HOST } = import.meta.env;

const isTackleboxLink = (obj: unknown): obj is TackleboxLink => {
  if (typeof obj !== 'object' || obj === null) return false;

  const o = obj as Record<string, unknown>;

  return typeof o.tacklebox_id === 'string' && typeof o.tito_id === 'string';
};

export interface NavbarMailboxTackleboxNotificationProps {
  notification: UserNotification;
  onNavigate: (id: string) => void;
}
const NavbarMailboxTackleboxNotification = ({ notification, onNavigate }: NavbarMailboxTackleboxNotificationProps) => {
  const theme = useTheme();

  const datum = useMemo(() => {
    if (!notification.data) return null;
    if (!isTackleboxLink(notification.data)) return null;
    return notification.data;
  }, [notification.data]);

  const createdAtRelative = useMemo(() => {
    return dayjs(notification.created_at).fromNow();
  }, [notification.created_at]);

  const onDownload = useCallback(async () => {
    if (!datum) {
      toast.error('Invalid notification data, unable to download file');
      return;
    }

    const jobId = datum.tacklebox_id;

    try {
      // Get access token for authentication
      const access_token = await sec.getAccessTokenSilently()();

      // Make direct fetch request to avoid RTK Query storing blob in state
      window.open(`${VITE_SERVICE_HOST}/v0/tacklebox/download/csv/${jobId}?bearer_token=${access_token}`, '_blank');

      onNavigate(notification.id);
    } catch (err) {
      console.error(err);
      toast.error('Failed to download file');
    }
  }, [datum, notification.id, onNavigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        p: 2,
        flexDirection: 'column',
        gap: 0.5
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2
          // pt: 1,
          // pb: 1
        }}
      >
        <Typography
          sx={{
            // \n
            flex: 1,
            color: 'text.secondary',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '166%',
            letterSpacing: '0px'
          }}
        >
          Tacklebox activity
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            alignItems: 'center'
          }}
        >
          <Typography
            sx={{
              // \n
              color: 'text.secondary',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '166%',
              letterSpacing: '0px'
            }}
          >
            {createdAtRelative}
          </Typography>
          {notification.seen ? (
            <Box sx={{ width: 8 }} />
          ) : (
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main
              }}
            />
          )}
        </Box>{' '}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontWeight: 400,
            fontSize: '14px',
            letterSpacing: '0px',
            lineHeight: '143%'
          }}
        >
          {notification.message}
        </Typography>
        <Fab size="small" color="primary" sx={{ color: 'white' }} onClick={onDownload}>
          <DownloadFilled />
        </Fab>
      </Box>
    </Box>
  );
};

export default NavbarMailboxTackleboxNotification;
